import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const YesBankPersonalLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "Is this calculator exclusive to Yes Bank loans only?",
        "answer": "While tailored for Yes Bank personal loans, you can use the calculator to simulate EMIs for any bank by adjusting interest rates and tenure accordingly."
      },
      {
        "question": "How is this calculator helpful for planning prepayments?",
        "answer": "You can estimate how much interest you save by shortening the tenure manually or adjusting the principal balance post prepayment."
      },
      {
        "question": "Can I use this calculator for a top-up personal loan?",
        "answer": "Yes, just add the top-up amount to the principal and enter the revised tenure to calculate your updated EMI structure."
      },
      {
        "question": "Will the EMI change if I miss a payment?",
        "answer": "The EMI does not change unless renegotiated, but missing payments can incur penalties and affect your credit score adversely."
      },
      {
        "question": "Can I download the amortisation chart?",
        "answer": "Yes, TheFinMaster calculator provides downloadable schedules for your financial record-keeping and planning."
      },
      {
        "question": "Does Yes Bank allow flexible repayment tenure?",
        "answer": "Yes, Yes Bank typically offers personal loan tenure from 12 to 60 months based on your eligibility and income profile."
      },
      {
        "question": "Is the EMI fixed for the entire tenure?",
        "answer": "If your loan has a fixed rate of interest, then the EMI remains constant throughout. Floating rate loans may result in changes."
      },
      {
        "question": "Can the calculator reflect GST or other processing charges?",
        "answer": "The calculator does not include external charges like GST or file fees. It only shows EMI, total repayment, and interest."
      },
      {
        "question": "How do I ensure I’m eligible for the EMI I see here?",
        "answer": "Check your income, existing obligations, and credit score. Banks evaluate your repayment capacity before sanctioning the EMI."
      },
      {
        "question": "Is the calculator accessible on mobile?",
        "answer": "Yes, it is fully mobile-responsive and works smoothly on phones, tablets, and desktops without any installation."
      },
      {
        "question": "Can I calculate EMI for joint personal loans?",
        "answer": "Yes, enter the combined loan amount. EMI remains the same; repayment responsibility is usually shared between applicants."
      },
      {
        "question": "Is any login or sign-up required to use the tool?",
        "answer": "No login is required. The calculator is privacy-first and stores no personal data while offering unlimited free use."
      },
      {
        "question": "Can I use it offline?",
        "answer": "No, the calculator functions online only. However, you can save or screenshot the results for offline reference."
      },
      {
        "question": "Is the EMI shown inclusive of insurance?",
        "answer": "No, personal loan EMI only includes principal and interest. Loan insurance, if taken, is charged separately."
      },
      {
        "question": "Can I negotiate interest rates with Yes Bank?",
        "answer": "Yes, applicants with excellent credit scores and stable income may negotiate for better interest rates and terms."
      }
    ]
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>Yes Bank Personal Loan EMI Calculator - Quick & Accurate EMI Estimator</title>
          <meta  name='description' content='Use our Yes Bank personal loan EMI Calculator to calculate your monthly payments instantly. Plan your loan better with accurate EMI estimation.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/yes-bank-personal-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
        <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
               Yes Bank Personal Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
           <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          Yes Bank Personal Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Plan Your Yes Bank Personal Loan Repayments with TheFinMaster’s EMI Calculator
      </h2>
      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
        Taking a personal loan is often the most practical way to finance important life events—be it a wedding, travel, home renovation, education, or emergency medical needs. But before committing to a loan, it’s vital to understand your monthly repayment obligations and how they align with your financial goals. This is where TheFinMaster’s Yes Bank Personal Loan EMI Calculator becomes an essential planning tool.
        Our calculator helps you estimate your monthly EMIs accurately, understand the amortisation structure, simulate different loan scenarios, and stay on top of your repayment strategy. Designed for ease of use and precision, it gives you the financial clarity and control you need before you proceed with your loan application.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is a Personal Loan EMI?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        A Personal Loan EMI (Equated Monthly Installment) is the fixed monthly payment made by a borrower to the bank to repay the loan amount along with interest. This amount is a combination of the principal (the borrowed amount) and interest (charged on the outstanding balance).
        During the early part of the repayment period, a larger portion of your EMI goes toward paying off the interest. As the loan matures, the principal component increases while the interest portion decreases. Understanding this shift is crucial to managing your cash flow and planning any prepayments in the future.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is the Yes Bank Personal Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The Yes Bank Personal Loan EMI Calculator hosted by TheFinMaster is a free, instant online tool that helps you determine your monthly EMI based on three primary inputs:
      </p>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li>Loan Amount</li>
        <li>Interest Rate</li>
        <li>Loan Tenure</li>
      </ul>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Once you enter these, the calculator gives you:
      </p>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li>Your monthly EMI</li>
        <li>Total interest payable</li>
        <li>Total repayment amount</li>
        <li>A visual amortisation chart for deeper clarity</li>
      </ul>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Whether you are at the consideration stage or preparing for disbursement, this calculator helps you evaluate loan affordability and choose the right repayment structure.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Why Use TheFinMaster’s Yes Bank EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Using our Yes Bank EMI Calculator offers multiple benefits:
      </p>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Instant Calculations:</strong> Get results in less than a second without the need for any personal information.</li>
        <li><strong>Real-Time Adjustments:</strong> Modify the loan parameters and instantly see how they impact your EMI.</li>
        <li><strong>Transparency First:</strong> No hidden charges or misleading figures—what you see is what you plan for.</li>
        <li><strong>Informed Decisions:</strong> Understand the total cost of borrowing and how long it will take to repay comfortably.</li>
        <li><strong>User Friendly:</strong> Accessible on any device—be it desktop or mobile—with a clean and intuitive interface.</li>
        <li><strong>Visualisation Tools:</strong> Break down each EMI and track principal vs. interest repayment over time.</li>
      </ul>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        This calculator is particularly helpful if you're comparing multiple loan options or planning to make prepayments in the near future.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        How Does the Yes Bank Personal Loan EMI Calculator Work?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The EMI is calculated using this standard formula:
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI = [P × R × (1 + R)^N] / [(1 + R)^N – 1]
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Where:
      </p>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li>P = Principal amount</li>
        <li>R = Monthly interest rate (Annual Rate ÷ 12 ÷ 100)</li>
        <li>N = Loan tenure in months</li>
      </ul>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <strong>Example Calculation:</strong><br />
        Suppose you take a loan of ₹5,00,000 from Yes Bank at an interest rate of 12.5% p.a. for 5 years (60 months).<br />
        P = ₹5,00,000<br />
        R = 1.0417% (12.5 ÷ 12)<br />
        N = 60<br />
        EMI = ₹11,249 (approx.)<br />
        Total Repayment = ₹6,74,940<br />
        Total Interest Payable = ₹1,74,940<br />
        This helps you assess the actual financial commitment before taking the loan.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Sample Amortisation Schedule (First 12 Months)
      </h2>
      <div className="mt-6">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">EMI (₹)</th>
                <th className="border px-4 py-2 text-left">Interest (₹)</th>
                <th className="border px-4 py-2 text-left">Principal (₹)</th>
                <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "11,249", "5,208", "6,041", "4,93,959"],
                ["2", "11,249", "5,141", "6,108", "4,87,851"],
                ["3", "11,249", "5,073", "6,176", "4,81,675"],
                ["4", "11,249", "5,002", "6,247", "4,75,428"],
                ["5", "11,249", "4,928", "6,321", "4,69,107"],
                ["6", "11,249", "4,851", "6,398", "4,62,709"],
                ["7", "11,249", "4,773", "6,476", "4,56,233"],
                ["8", "11,249", "4,690", "6,559", "4,49,674"],
                ["9", "11,249", "4,605", "6,644", "4,43,030"],
                ["10", "11,249", "4,516", "6,733", "4,36,297"],
                ["11", "11,249", "4,423", "6,826", "4,29,471"],
                ["12", "11,249", "4,327", "6,922", "4,22,549"]
              ].map(([month, emi, interest, principal, balance], index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{month}</td>
                  <td className="border px-4 py-2">{emi}</td>
                  <td className="border px-4 py-2">{interest}</td>
                  <td className="border px-4 py-2">{principal}</td>
                  <td className="border px-4 py-2">{balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4 mt-4">
          {[
            ["1", "11,249", "5,208", "6,041", "4,93,959"],
            ["2", "11,249", "5,141", "6,108", "4,87,851"],
            ["3", "11,249", "5,073", "6,176", "4,81,675"],
            ["4", "11,249", "5,002", "6,247", "4,75,428"],
            ["5", "11,249", "4,928", "6,321", "4,69,107"],
            ["6", "11,249", "4,851", "6,398", "4,62,709"],
            ["7", "11,249", "4,773", "6,476", "4,56,233"],
            ["8", "11,249", "4,690", "6,559", "4,49,674"],
            ["9", "11,249", "4,605", "6,644", "4,43,030"],
            ["10", "11,249", "4,516", "6,733", "4,36,297"],
            ["11", "11,249", "4,423", "6,826", "4,29,471"],
            ["12", "11,249", "4,327", "6,922", "4,22,549"]
          ].map(([month, emi, interest, principal, balance], index) => (
            <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
              <div><span className="font-semibold">Month:</span> {month}</div>
              <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
              <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
              <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
              <div><span className="font-semibold">Outstanding Balance (₹):</span> {balance}</div>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        This structure continues monthly until the loan is paid off. You can download the full amortisation schedule for more visibility.
      </p>
    </div>
    <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Factors That Impact Your EMI
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Here are the key factors that influence your EMI and repayment journey:
      </p>
      <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount:</strong> The principal amount directly impacts your EMI. Borrow only what you need to ensure manageable repayments.</li>
        <li><strong>Interest Rate:</strong> Even a small variation in the interest rate can significantly change your monthly EMI and total interest burden.</li>
        <li><strong>Tenure:</strong> Longer tenures mean smaller EMIs but more interest overall. Shorter tenures mean higher EMIs but lower interest.</li>
        <li><strong>Credit Score:</strong> Applicants with strong credit profiles may receive lower interest rates, directly reducing their EMI.</li>
        <li><strong>Income Profile:</strong> A steady income ensures the bank’s confidence and may allow for flexible terms suited to your financial profile.</li>
        <li><strong>Prepayment Option:</strong> Making part-prepayments helps reduce the outstanding principal and shortens the tenure or EMI burden.</li>
      </ol>
    </div>
  </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
                 {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default YesBankPersonalLoanEmiCal;
